import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Switch } from 'react-router-dom';
import PrivateRoute from './components/Routes/PrivateRoute';
import PublicRoute from './components/Routes/PublicRoute';

import PlanInfo from 'components/Common/PlanInfo';
import Typography from 'components/Atoms/Typography';
import useApp from './hooks/useApp';
import ErrorPage from './components/ErrorPage';
import ConfigureTest from './components/Onboarding/ConfigureTest';
import ConfigureTestYamlBased from './components/Onboarding/ConfigureTestYamlBased';
import Loader from 'components/Loader';
// eslint-disable-next-line
import ComponentTraceLoader from 'components/ComponentTraceLoader';
import { useLocation } from 'react-router-dom';

import Artifacts from 'pages/artifacts';

import { Path } from 'constants/routes';
import QuickRun from 'components/Onboarding/QuickRun';
import { FF_WORKFLOW } from 'constants/feature-flags';
import { FF_PROJECTS } from 'constants/feature-flags';

import './assets/styles/routes.css';
import useFeatureFlags from 'hooks/useFeatureFlags';
import history from 'utils/history';
import { SideBarMenuClasses } from 'constants/common';

const PRIVATE_TYPE = 'private';
const PUBLIC_TYPE = 'public';

const SIDEBAR_HIDDEN_CLASS_NAME = 'ltch-hidden';

const Privacy = React.lazy(() => import('./components/Privacy'));

const HyperExecute = React.lazy(() =>
  import(/* webpackPrefetch: true */ './components/HyperExecute')
);

const DesignSystem = React.lazy(() => import('./components/DesignSystem'));

const HypertestJobDetail = React.lazy(() =>
  import('./components/HypertestJobDetail')
);

const ProjectListing = React.lazy(() => import('./pages/projects/listing'));

const ProjectDetail = React.lazy(() => import('./pages/projects/detail'));

const HomePage = React.lazy(() => import('./components/Onboarding/HomePage'));

const TrialUserPage = React.lazy(() => import('./components/TrialUserPage'));

const ScheduledBuild = React.lazy(() => import('./pages/scheduled-job'));

// const ConfigureTest = React.lazy(() =>
//   import('./components/Onboarding/ConfigureTest')
// );

// const ConfigureTestYamlBased = React.lazy(() =>
//   import('./components/Onboarding/ConfigureTestYamlBased')
// );

const ExcludeExactMatchRoutes = [Path.HYPEREXECUTE, Path.JOBS];

const Insights = () => {
  return <h2 className="insight-coming">Coming Soon...</h2>;
};

const routes = [
  {
    path: ['/', '/v2/:tab'],
    type: PRIVATE_TYPE,
  },
  {
    path: Path.INSIGHTS.Path,
    Component: Insights,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.INSIGHTS.WithParam,
    Component: Insights,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.TASK,
    Component: HypertestJobDetail,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.PROJECT_LISTING,
    Component: ProjectListing,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.WORKFLOW,
    Component: ScheduledBuild,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.PROJECT_DETAILS,
    Component: ProjectDetail,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.ARTIFACT.WithParam,
    Component: Artifacts,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.HYPEREXECUTE,
    Component: HyperExecute,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.PRIVACY,
    Component: Privacy,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.DESIGN_SYSTEM,
    Component: DesignSystem,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.QUICK_START,
    Component: HomePage,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.TRY_NOW,
    Component: TrialUserPage,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.ONBOARD,
    Component: TrialUserPage,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.QUICK_START_QUICK_RUN.WithParam,
    Component: QuickRun,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.QUICK_START_CONFIGURE_TEST.WithParam,
    Component: ConfigureTest,
    type: PUBLIC_TYPE,
  },
  {
    path: Path.QUICK_START_YAML_BASED_TEST.WithParam,
    Component: ConfigureTestYamlBased,
    type: PUBLIC_TYPE,
  },
  {
    path: '/*',
    Component: ErrorPage,
    type: PUBLIC_TYPE,
  },
];
const UrlToClassMapping = {
  [Path.JOBS]: SideBarMenuClasses.JOBS,
  [Path.TASK]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START_CONFIGURE_TEST.Path]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START_QUICK_RUN.Path]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START_YAML_BASED_TEST.Path]: SideBarMenuClasses.JOBS,
  [Path.ONBOARD]: SideBarMenuClasses.JOBS,
  [Path.PROJECT_LISTING]: SideBarMenuClasses.PROJECTS,
  [Path.PROJECT_DETAILS]: SideBarMenuClasses.PROJECTS,
  [Path.WORKFLOW]: SideBarMenuClasses.WORKFLOW,
};
const SideNavData = {
  Jobs: {
    CLASS_NAME: SideBarMenuClasses.JOBS,
    PATH: Path.JOBS,
  },
  Projects: {
    CLASS_NAME: SideBarMenuClasses.PROJECTS,
    PATH: Path.PROJECT_LISTING,
    FEATURE_FLAG: FF_PROJECTS,
  },
  Workflow: {
    CLASS_NAME: SideBarMenuClasses.WORKFLOW,
    PATH: Path.WORKFLOW,
    FEATURE_FLAG: FF_WORKFLOW,
  },
};

const Routes = ({ isOldHeader, children }) => {
  const { userInfo = {}, orgPreference = {} } = useApp();
  const featureFlags = useFeatureFlags();
  const visibleMenusList = getMenusToShow();
  const enabledMenusRef = useRef(visibleMenusList);
  const retryCount = useRef(0);
  const userDataRef = useRef(null);
  const location = useLocation();
  const loactionRef = useRef(location);
  const { data: userInfoData } = userInfo;
  const navItemRetryCount = useRef(0);

  const { error: opError } = orgPreference;

  const isThingsLoading = !userInfoData || opError === null;

  useEffect(() => {
    userDataRef.current = userInfoData;
  }, [userInfoData]);

  useEffect(() => {
    enabledMenusRef.current = visibleMenusList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags]);

  function getMenusToShow() {
    const arr = [];
    Object.keys(SideNavData).forEach((key) => {
      const data = SideNavData[key];
      if (data.FEATURE_FLAG && featureFlags[data.FEATURE_FLAG]) {
        arr.push(data);
      }
    });
    return arr;
  }

  function handleActiveClass(activeClass) {
    const subNavClasses = Object.values(SideBarMenuClasses);
    subNavClasses.forEach((classValue) => {
      const ele = document.getElementsByClassName(classValue)[0];
      if (classValue !== activeClass) {
        ele?.classList?.remove('active');
      } else {
        ele?.classList?.add('active');
      }
    });
  }

  function handleNavItemClick(pathName, activeClass) {
    return (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      history.push(pathName);
      handleActiveClass(activeClass);
    };
  }

  function handleNavigation() {
    const menus = Object.values(SideNavData);
    for (let i = 0; i < menus.length; i++) {
      let ele = document.getElementsByClassName(menus[i].CLASS_NAME)[0];
      if (!ele && navItemRetryCount.current < 20) {
        navItemRetryCount.current = navItemRetryCount.current + 1;
        setTimeout(() => {
          handleNavigation();
        }, 10);
        break;
      }
      ele?.removeEventListener(
        'click',
        handleNavItemClick(menus[i].PATH, menus[i].CLASS_NAME)
      );
      ele?.addEventListener(
        'click',
        handleNavItemClick(menus[i].PATH, menus[i].CLASS_NAME)
      );
    }
  }

  function handleProductNavExpanded() {
    retryCount.current = 0;
    navItemRetryCount.current = 0;
    handleNavigation();
    showMenuInSidebar();
  }

  useEffect(() => {
    loactionRef.current = location;
  }, [location]);

  useEffect(() => {
    window.addEventListener('productNavExpanded', handleProductNavExpanded);
    return () => {
      retryCount.current = 0;
      navItemRetryCount.current = 0;
      window.removeEventListener('productNavExpanded', () => {});
      const menus = Object.values(SideNavData);
      for (let i = 0; i < menus.length; i++) {
        let ele = document.getElementsByClassName(menus[i].CLASS_NAME)[0];
        ele?.removeEventListener(
          'click',
          handleNavItemClick(menus[i].PATH, menus[i].CLASS_NAME)
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
      retryCount.current = 0;
      showMenuInSidebar();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    navItemRetryCount.current = 0;
    handleNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function showMenuInSidebar(showProduct, showWorkflow) {
    for (let i = 0; i < enabledMenusRef.current.length; i++) {
      const value = enabledMenusRef.current[i];
      const ele = document.getElementsByClassName(value.CLASS_NAME)[0];
      if (!ele && retryCount.current < 20) {
        retryCount.current = retryCount?.current + 1;
        setTimeout(() => {
          showMenuInSidebar(showProduct, showWorkflow);
        }, 3);
        break;
      } else if (ele) {
        ele.classList.remove(SIDEBAR_HIDDEN_CLASS_NAME);
        window.setTimeout(() => {
          let activeClass = UrlToClassMapping[loactionRef.current?.pathname];
          if (!activeClass) {
            let baseLocationValue = loactionRef.current?.pathname.split('/');
            baseLocationValue.pop();
            activeClass = UrlToClassMapping[baseLocationValue?.join('/')];
          }
          handleActiveClass(activeClass);
        }, 2);
      }
    }
  }

  if (isThingsLoading) {
    return <Loader isSuspenseLoader={isOldHeader} />;
  }

  return <>{children}</>;
};

const RouteWrapper = ({ isOldHeader }) => {
  return (
    <>
      <HeaderInfo />
      <Routes isOldHeader={isOldHeader}>
        <Switch>
          {routes.map(({ path, Component, type, ...props }) => {
            if (type === PUBLIC_TYPE)
              return (
                <PublicRoute
                  key={path}
                  component={Component}
                  path={path}
                  exact={!ExcludeExactMatchRoutes.includes(path)}
                  {...props}
                />
              );
            if (type === PRIVATE_TYPE)
              return (
                <PrivateRoute
                  key={path}
                  component={Component}
                  path={path}
                  exact
                  {...props}
                />
              );
            return path;
          })}
        </Switch>
      </Routes>
    </>
  );
};

function HeaderInfo() {
  const [is, setIs] = useState(false);

  useEffect(() => {
    function check() {
      var ele = document.getElementsByClassName('navbar__header')[0];
      if (!ele) {
        setTimeout(check, 10);
      } else {
        setIs(true);
      }
    }

    check();
  }, []);

  if (!is) {
    return null;
  }

  var ele = document.getElementsByClassName('navbar__header')[0];
  return ReactDOM.createPortal(
    <div className="common-header-hyp-info">
      <Typography variant="h1" extraClasses="common-header-title">
        HyperExecute
      </Typography>
      <PlanInfo />
    </div>,
    ele
  );
}

export default RouteWrapper;
